import { DeleteOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useApplicationContext } from "components/Application";
import { capitalize } from "lodash";
import React, { Fragment } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import tw from "twin.macro";

const orderDocumentQuery = gql`
  query OrderDocument {
    accountSession {
      currentUser {
        cartDocument {
          id
          code
          notes
          paymentMethod {
            id
            code
            nameEn
            nameIt
          }
          rows {
            id
            item {
              id
              code
              available
              color {
                id
                code
                nameEn
                nameIt
                patch
              }
            }
            referenceItem {
              id
              code
              cover
              nameEn
              nameIt
              shortDescriptionEn
              shortDescriptionIt
              brand {
                id
              }
              season {
                id
              }
              sizeGroup {
                id
                code
                sizes {
                  id
                  code
                  value
                }
              }
            }
            sizeQuantities {
              id
              quantity
              size {
                id
                code
              }
            }
          }
        }
      }
    }
  }
`;

const createOrderRowMutation = gql`
  mutation($input: CreateOrderRowInput!) {
    createOrderRow(input: $input) {
      row {
        id
        item {
          id
          code
        }
        referenceItem {
          id
          code
        }
        sizeQuantities {
          id
          quantity
          size {
            id
            code
          }
        }
      }
    }
  }
`;

const updateOrderRowMutation = gql`
  mutation($id: ID!, $input: UpdateOrderRowInput!) {
    updateOrderRow(id: $id, input: $input) {
      row {
        id
        item {
          id
          code
        }
        referenceItem {
          id
          code
        }
        sizeQuantities {
          id
          quantity
          size {
            id
            code
          }
        }
      }
    }
  }
`;

const deleteOrderRowMutation = gql`
  mutation($id: ID!) {
    deleteOrderRow(id: $id) {
      successful
    }
  }
`;

type ItemCartRowProps = {
  item: any;
  variant: any;
  sizes: any[];
  stock: boolean;
  orderable: boolean;
  setActiveImage: (image: string) => void;
};

export const ItemCartRow: React.FC<ItemCartRowProps> = (props) => {
  const { item, variant, sizes, stock, setActiveImage, orderable } = props;
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();

  const { i18n } = useTranslation();
  const languageSuffix = capitalize(i18n.language);

  const { register, handleSubmit, formState, reset } = useForm();
  const { isDirty } = formState;

  const { loading, data } = useQuery(orderDocumentQuery);

  const [execCreate] = useMutation(createOrderRowMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      console.error(graphQLErrors);
    },
    onCompleted: (data) => {
      const row = data.createOrderRow.row;
      const sizeQuantities = row.sizeQuantities;

      let allSizeQuantities: any[] = [];
      sizes.forEach((size: any) => {
        let foundSizeQuantity: any;
        sizeQuantities.forEach((sizeQuantity: any) => {
          if (sizeQuantity.size.id === size.id) {
            foundSizeQuantity = sizeQuantity;
          }
        });
        if (foundSizeQuantity) allSizeQuantities.push({ sizeId: size.id, quantity: foundSizeQuantity.quantity });
        else allSizeQuantities.push({ sizeId: size.id, quantity: 0 });
      });
      toast.success(i18n.language === "it" ? "Riga del carrello creata!" : "Cart row created!", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      reset({ ...row, sizeQuantities: allSizeQuantities });
    },
    refetchQueries: [{ query: orderDocumentQuery }],
    awaitRefetchQueries: true,
  });

  const [execUpdate] = useMutation(updateOrderRowMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      console.error(graphQLErrors);
    },
    onCompleted: (data) => {
      const row = data.updateOrderRow.row;
      const sizeQuantities = row.sizeQuantities;
      let allSizeQuantities: any[] = [];
      sizes.forEach((size: any) => {
        let foundSizeQuantity: any;
        sizeQuantities.forEach((sizeQuantity: any) => {
          if (sizeQuantity.size.id === size.id) {
            foundSizeQuantity = sizeQuantity;
          }
        });
        if (foundSizeQuantity) allSizeQuantities.push({ sizeId: size.id, quantity: foundSizeQuantity.quantity });
        else allSizeQuantities.push({ sizeId: size.id, quantity: 0 });
      });
      toast.success(i18n.language === "it" ? "Riga del carrello aggiornata!" : "Cart row updated!", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      reset({ ...row, sizeQuantities: allSizeQuantities });
    },
    refetchQueries: [{ query: orderDocumentQuery }],
    awaitRefetchQueries: true,
  });

  const [execDelete] = useMutation(deleteOrderRowMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      console.error(graphQLErrors);
    },
    onCompleted: (data) => {
      let allSizeQuantities: any[] = [];
      sizes.forEach((size: any) => {
        allSizeQuantities.push({ sizeId: size.id, quantity: 0 });
      });
      toast.success(i18n.language === "it" ? "Riga del carrello eliminata!" : "Cart row removed!", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      reset({
        documentId: document.id,
        itemId: variant.id,
        referenceItemId: item.id,
        sizeQuantities: allSizeQuantities,
      });
    },
    refetchQueries: [{ query: orderDocumentQuery }],
    awaitRefetchQueries: true,
  });

  if (loading) return <Fragment></Fragment>;

  const {
    accountSession: {
      currentUser: { cartDocument },
    },
  } = data;
  const document = cartDocument;
  const { rows } = document;

  let orderRow: any;
  rows.forEach((row: any) => {
    if (row.item.id === variant.id) orderRow = row;
  });

  const onSubmit = (data: any) => {
    const { sizeQuantities } = data;

    let cleanSizeQuantities: any[] = [];
    sizeQuantities.forEach((sizeQuantity: any) => {
      const parsedQuantity = parseInt(sizeQuantity.quantity);
      if (parsedQuantity > 0) {
        cleanSizeQuantities.push({
          sizeId: sizeQuantity.sizeId,
          quantity: parsedQuantity,
        });
      }
    });

    const cleanData = {
      ...data,
      sizeQuantities: cleanSizeQuantities,
    };

    !orderRow
      ? execCreate({ variables: { input: cleanData } })
      : execUpdate({ variables: { id: orderRow.id, input: cleanData } });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input type="hidden" name="documentId" defaultValue={document.id} ref={register} />
      <input type="hidden" name="itemId" defaultValue={variant.id} ref={register} />
      <input type="hidden" name="referenceItemId" defaultValue={item.id} ref={register} />
      <table style={{ borderCollapse: "initial", borderSpacing: "5px" }}>
        <tbody>
          <tr key={variant.id}>
            <td style={{ width: "150px" }}>
              {variant.color.code} - {variant.color[`name${languageSuffix}`]}
              {stock && <div tw="text-center h-8 pt-1"></div>}
            </td>
            <td>
              <div
                onClick={() => {
                  if (variant.images.length > 0) setActiveImage(variant.images[0].image);
                  else if (item.colorGuide && item.colorGuide !== "") setActiveImage(item.colorGuide);
                }}
              >
                <span
                  tw="cursor-pointer"
                  style={{
                    backgroundImage: `url(${appEndpointStore}${variant.color.patch})`,
                    display: "block",
                    width: "32px",
                    height: "32px",
                    backgroundSize: "cover",
                  }}
                />
              </div>
              {stock && <div tw="text-center h-8 pt-1"></div>}
            </td>
            {variant.available ? (
              <Fragment>
                {sizes.map((size: any, index: number) => {
                  let currentQuantity = 0;
                  orderRow?.sizeQuantities?.forEach((sizeQuantity: any) => {
                    if (sizeQuantity.size.id === size.id) currentQuantity = sizeQuantity.quantity;
                  });

                  let available = !stock;
                  let availability = 0;
                  if (stock)
                    variant.stocks?.forEach((stock: any) => {
                      if (stock?.size?.id === size.id && stock.quantity > 0) {
                        available = true;
                        availability = stock.quantity;
                      }
                    });

                  return (
                    <td key={size.id} tw="relative">
                      <input
                        type="hidden"
                        name={`sizeQuantities[${index}].sizeId`}
                        defaultValue={size.id}
                        ref={register}
                      />
                      <input
                        type="number"
                        onFocus={(e) => e.target.select()}
                        css={[
                          tw`border-default border-gray-900 text-gray-900 text-center`,
                          !available && tw`text-gray-500 border-gray-500`,
                        ]}
                        style={{ width: "32px", height: "32px" }}
                        min={0}
                        max={!stock ? 999 : availability}
                        name={`sizeQuantities[${index}].quantity`}
                        disabled={!available}
                        defaultValue={currentQuantity}
                        ref={register}
                      />
                      {stock && <div tw="text-center h-8 pt-1">{availability > 0 && `(${availability})`}</div>}
                    </td>
                  );
                })}
                <td>
                  {orderable && (
                    <button
                      css={[
                        !isDirty && tw`border-default border-gray-500 text-gray-500 cursor-not-allowed`,
                        isDirty &&
                          tw`border-default border-gray-900 text-gray-900 hover:(bg-green-500 border-green-500 text-white)`,
                      ]}
                      style={{ width: "32px", height: "30px", paddingBottom: "2px" }}
                      disabled={!isDirty}
                      type="submit"
                    >
                      <ShoppingCartOutlined style={{ fontSize: "18px" }} />
                    </button>
                  )}
                  {stock && <div tw="text-center h-8 pt-1"></div>}
                </td>
                <td>
                  <button
                    onClick={() => execDelete({ variables: { id: orderRow.id } })}
                    css={[
                      !orderRow && tw`border-default border-gray-500 text-gray-500 cursor-not-allowed`,
                      orderRow &&
                        tw`border-default border-red-500 text-red-500 border-red-500 hover:(bg-red-500 text-white)`,
                    ]}
                    style={{ width: "32px", height: "30px", paddingBottom: "2px" }}
                    disabled={!orderRow}
                    type="button"
                  >
                    <DeleteOutlined style={{ fontSize: "18px" }} />
                  </button>
                  {stock && <div tw="text-center h-8 pt-1"></div>}
                </td>
              </Fragment>
            ) : (
              <td colSpan={sizes.length + 2}>
                <span style={{ marginTop: "5px", display: "block" }}>{i18n.language === "it" ? "NON DISPONIBILE" : "NOT AVAILABLE"}</span>
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </form>
  );
};
